import { useEffect, useMemo, useRef, useState } from "react";

import {
  ArrowDown,
  ArrowSquareOut,
  CaretDown,
  EyeSlash,
  MagnifyingGlass,
  ThumbsUp,
  TrashSimple,
} from "@phosphor-icons/react";
import {
  Badge,
  DataRow,
  DataTable,
  IconButton,
  LoadingOverlay,
  PaginationFooter,
  ProfileTabs,
  SearchField,
  buildPageList,
} from "@breakingthelines/design-system";

import { handleTabArrowKeyDown } from "@/components/common/tab-keyboard-nav";
import {
  listContentFlags,
  listThoughtFlags,
  reviewContentFlag,
  reviewThoughtFlag,
  type ContentFlagItem,
  type ModerationActionValue,
  type PageInfo,
  type ThoughtFlagItem,
} from "@/lib/admin-api";
import { useRealtimeChannel } from "@/hooks/use-realtime-channel";

import styles from "./moderation-queue.module.css";

const TABS = ["Pending", "Reviewed", "Dismissed"] as const;
type Tab = (typeof TABS)[number];

const TAB_STATUS_MAP: Record<Tab, string> = {
  Pending: "FLAG_STATUS_PENDING",
  Reviewed: "FLAG_STATUS_REVIEWED",
  Dismissed: "FLAG_STATUS_DISMISSED",
};

// Content flags and thought flags come from two different services, each paging
// its own list. The queue used to fetch page N of both and sort the merged page
// by createdAt, which cannot produce a global order: page 2 of content can
// easily be newer than page 1 of thoughts, and the pagination total was the sum
// of two independent totals, so "page 4 of 12" described no real sequence.
//
// One source at a time is the smallest honest fix. Each tab is a single ordered
// list with its own real total, and no ordering is claimed across the two.
const SOURCES = ["Content", "Thoughts"] as const;
type Source = (typeof SOURCES)[number];

const PER_PAGE_OPTIONS = [10, 20, 50];

const REASON_OPTIONS = [
  { value: "all", label: "All" },
  { value: "FLAG_REASON_SPAM", label: "Spam" },
  { value: "FLAG_REASON_HARASSMENT", label: "Harassment" },
  { value: "FLAG_REASON_MISINFORMATION", label: "Misinformation" },
  { value: "FLAG_REASON_INAPPROPRIATE", label: "Inappropriate" },
  { value: "FLAG_REASON_COPYRIGHT", label: "Copyright" },
  { value: "FLAG_REASON_OTHER", label: "Other" },
] as const;
type ReasonFilter = (typeof REASON_OPTIONS)[number]["value"];

// A moderation_action paired with the FlagStatus it resolves the flag to.
type ReviewAction = {
  action: ModerationActionValue;
  status: string;
};

const REVIEW_ACTIONS: Record<"dismiss" | "derank" | "hide" | "remove", ReviewAction> = {
  dismiss: { action: "MODERATION_ACTION_DISMISS", status: "FLAG_STATUS_DISMISSED" },
  // Derank and Hide both drop the item from every public/discovery surface
  // (trending/feeds/search) while it stays on the author's own profile and its
  // direct URL still 200s — Derank is the automated reach-control signal,
  // Hide is the explicit moderator call; kept distinct for audit/analytics.
  // Remove is the only action that 404s everywhere, including the profile —
  // reserve it for manual/severe cases.
  derank: { action: "MODERATION_ACTION_DERANK", status: "FLAG_STATUS_REVIEWED" },
  hide: { action: "MODERATION_ACTION_HIDE", status: "FLAG_STATUS_REVIEWED" },
  remove: { action: "MODERATION_ACTION_REMOVE", status: "FLAG_STATUS_REVIEWED" },
};

type QueueRow =
  | { kind: "content"; flag: ContentFlagItem }
  | { kind: "thought"; flag: ThoughtFlagItem };

type FlaggedBy = ContentFlagItem["flaggedBy"];

function useDebouncedValue<T>(value: T, delay = 300) {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const handle = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(handle);
  }, [value, delay]);

  return debounced;
}

function rowId(row: QueueRow): string {
  return `${row.kind}:${row.flag.id}`;
}

function rowReporter(by: FlaggedBy, fallbackId: string): string {
  return by?.displayName || by?.username || fallbackId;
}

function rowTitle(row: QueueRow): string {
  if (row.kind === "content") {
    return row.flag.contentTitle || row.flag.contentId;
  }
  return row.flag.thoughtPreview || row.flag.thoughtId;
}

function rowViewHref(row: QueueRow): string {
  return row.kind === "content"
    ? `/content/${row.flag.contentId}`
    : `/thoughts/${row.flag.thoughtId}`;
}

function formatFlagReason(reason: string): string {
  const map: Record<string, string> = {
    FLAG_REASON_SPAM: "Spam",
    FLAG_REASON_HARASSMENT: "Harassment",
    FLAG_REASON_MISINFORMATION: "Misinformation",
    FLAG_REASON_INAPPROPRIATE: "Inappropriate",
    FLAG_REASON_COPYRIGHT: "Copyright",
    FLAG_REASON_OTHER: "Other",
    FLAG_REASON_TYPO: "Typo",
    FLAG_REASON_FACTUAL_ERROR: "Factual Error",
    FLAG_REASON_TTS_PRONUNCIATION: "TTS Pronunciation",
    FLAG_REASON_SUGGESTION: "Suggestion",
  };
  return map[reason] ?? reason;
}

function formatDate(value?: string): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric",
  }).format(date);
}

function ReasonDropdown({
  value,
  onSelect,
}: {
  value: ReasonFilter;
  onSelect: (value: ReasonFilter) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleClick = (event: MouseEvent) => {
      if (!ref.current?.contains(event.target as Node)) setOpen(false);
    };
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    window.addEventListener("mousedown", handleClick);
    window.addEventListener("keydown", handleKey);
    return () => {
      window.removeEventListener("mousedown", handleClick);
      window.removeEventListener("keydown", handleKey);
    };
  }, [open]);

  const current = REASON_OPTIONS.find((option) => option.value === value);
  const label = value === "all" ? "Reason" : `Reason: ${current?.label ?? value}`;

  return (
    <div className={styles.dropdownWrapper} ref={ref}>
      <button
        type="button"
        className={`${styles.dropdown} ${value !== "all" ? styles.dropdownActive : ""}`}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className={styles.dropdownLabel}>{label}</span>
        <CaretDown className={styles.dropdownIcon} size={14} />
      </button>
      {open ? (
        <div className={styles.dropdownMenu} role="listbox">
          {REASON_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              role="option"
              aria-selected={option.value === value}
              className={`${styles.dropdownOption} ${option.value === value ? styles.dropdownOptionActive : ""}`}
              onClick={() => {
                onSelect(option.value);
                setOpen(false);
              }}
            >
              {option.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export function ModerationQueuePage() {
  const [activeTab, setActiveTab] = useState<Tab>("Pending");
  const [source, setSource] = useState<Source>("Content");
  const [searchInput, setSearchInput] = useState("");
  const query = useDebouncedValue(searchInput.trim());
  const [reason, setReason] = useState<ReasonFilter>("all");
  const [pageIndex, setPageIndex] = useState(1);
  const [pageSize, setPageSize] = useState(PER_PAGE_OPTIONS[0]);
  const [rows, setRows] = useState<QueueRow[]>([]);
  const [pageInfo, setPageInfo] = useState<PageInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState(0);

  // Any change to what is being listed restarts at page 1: page 4 of the old
  // filter names nothing in the new one.
  useEffect(() => {
    setPageIndex(1);
  }, [activeTab, source, query, reason]);

  // Search only exists on the content source (see listContentFlags), so leaving
  // a term behind when switching to Thoughts would show an unfiltered list under
  // a filled-in search box.
  useEffect(() => {
    if (source !== "Content") setSearchInput("");
  }, [source]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    const status = TAB_STATUS_MAP[activeTab];
    const reasonFilter = reason === "all" ? undefined : reason;

    const request =
      source === "Content"
        ? listContentFlags({ status, reason: reasonFilter, query, pageSize, page: pageIndex }).then(
            (response) => ({
              rows: (response.items ?? []).map((flag): QueueRow => ({ kind: "content", flag })),
              pageInfo: response.pageInfo ?? null,
            }),
          )
        : listThoughtFlags({ status, reason: reasonFilter, pageSize, page: pageIndex }).then(
            (response) => ({
              rows: (response.items ?? []).map((flag): QueueRow => ({ kind: "thought", flag })),
              pageInfo: response.pageInfo ?? null,
            }),
          );

    request
      .then((result) => {
        if (cancelled) return;
        setRows(result.rows);
        setPageInfo(result.pageInfo);
      })
      .catch((err: Error) => {
        if (cancelled) return;
        setError(err.message ?? "Failed to load flags");
        setRows([]);
        setPageInfo(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [activeTab, source, query, reason, pageIndex, pageSize, refreshToken]);

  useRealtimeChannel("admin:moderation:queue", () => {
    // Re-fetch on real-time events
    setRefreshToken((t) => t + 1);
  });

  const handleReview = (row: QueueRow, { action, status }: ReviewAction) => {
    const id = rowId(row);
    setActionLoading(id);
    const request =
      row.kind === "content"
        ? reviewContentFlag({ flagId: row.flag.id, status, moderationAction: action })
        : reviewThoughtFlag({ flagId: row.flag.id, status, moderationAction: action });

    request
      .then(() => {
        setRows((prev) => prev.filter((item) => rowId(item) !== id));
      })
      .catch((err: Error) => {
        setError(err.message ?? "Action failed");
      })
      .finally(() => {
        setActionLoading(null);
      });
  };

  // One source, one total. Falling back to the loaded row count keeps the label
  // honest when the service omits page_info rather than inventing a number.
  const totalCount = pageInfo?.totalCount ?? rows.length;
  const totalLabel = `Total ${source} Flags: ${totalCount}`;

  const totalPages = useMemo(() => {
    if (totalCount > 0) {
      return Math.max(1, Math.ceil(totalCount / pageSize));
    }
    return pageIndex;
  }, [totalCount, pageIndex, pageSize]);

  const pages = useMemo(() => buildPageList(totalPages, pageIndex), [totalPages, pageIndex]);
  const activePage = Math.min(pageIndex, totalPages);
  const hasPrevious = pageIndex > 1;
  const hasNext = pageInfo?.hasNextPage ?? (rows.length === pageSize && rows.length > 0);

  return (
    <div className={styles.page}>
      <h1 className={styles.title}>Moderation</h1>

      <div className={styles.toolbar}>
        <ProfileTabs
          role="tablist"
          aria-label="Flag status"
          tabs={TABS.map((tab) => ({ id: tab, label: tab }))}
          activeTab={activeTab}
          onKeyDown={handleTabArrowKeyDown}
          onTabChange={(tab) => setActiveTab(tab as Tab)}
        />

        <div className={styles.filters}>
          <ProfileTabs
            role="tablist"
            aria-label="Flag source"
            tabs={SOURCES.map((item) => ({ id: item, label: item }))}
            activeTab={source}
            onKeyDown={handleTabArrowKeyDown}
            onTabChange={(next) => setSource(next as Source)}
          />

          {source === "Content" ? (
            <SearchField
              className={styles.search}
              icon={<MagnifyingGlass className={styles.searchIcon} size={14} />}
              label="Search content flags"
              placeholder="Search title or details"
              value={searchInput}
              onChange={(event) => setSearchInput(event.currentTarget.value)}
            />
          ) : null}

          <ReasonDropdown value={reason} onSelect={setReason} />
        </div>
      </div>

      <section className={styles.listSection}>
        {loading ? <LoadingOverlay label="Loading flags..." /> : null}

        <DataTable
          columns="minmax(220px, 2fr) minmax(140px, 1fr) minmax(120px, 0.8fr) minmax(100px, 0.6fr) minmax(160px, 1fr)"
          header={["Flagged Content", "Reporter", "Reason", "Date", "Actions"]}
          headerClassName={styles.tableHeader}
        >
          {rows.map((row) => {
            const id = rowId(row);
            const busy = actionLoading === id;
            return (
              <DataRow key={id} className={styles.tableRow}>
                <div className={styles.flaggedContent}>
                  <div className={styles.flaggedTop}>
                    <Badge variant={row.kind === "content" ? "secondary" : "tintedWarn"}>
                      {row.kind === "content" ? "Content" : "Thought"}
                    </Badge>
                    <span className={styles.flaggedText}>{rowTitle(row)}</span>
                  </div>
                  {row.flag.details ? (
                    <span className={styles.flaggedHandle}>{row.flag.details}</span>
                  ) : null}
                </div>

                <span className={styles.reporter} data-label="Reporter">
                  {rowReporter(row.flag.flaggedBy, row.flag.flaggedById)}
                </span>

                <span className={styles.reasonBadge}>{formatFlagReason(row.flag.reason)}</span>

                <span className={styles.date} data-label="Date">
                  {formatDate(row.flag.createdAt)}
                </span>

                <div className={styles.actions}>
                  {activeTab === "Pending" ? (
                    <div className={styles.actionButtons}>
                      <IconButton
                        aria-label="Dismiss"
                        variant="ghost"
                        size="md"
                        onClick={busy ? undefined : () => handleReview(row, REVIEW_ACTIONS.dismiss)}
                      >
                        <ThumbsUp size={14} />
                      </IconButton>
                      <IconButton
                        aria-label="Derank (remove from discovery; URL still resolves; stays on the author's profile)"
                        variant="ghost"
                        size="md"
                        onClick={busy ? undefined : () => handleReview(row, REVIEW_ACTIONS.derank)}
                      >
                        <ArrowDown size={14} />
                      </IconButton>
                      <IconButton
                        aria-label="Hide (remove from public surfaces; stays on the author's profile)"
                        variant="ghost"
                        size="md"
                        onClick={busy ? undefined : () => handleReview(row, REVIEW_ACTIONS.hide)}
                      >
                        <EyeSlash size={14} />
                      </IconButton>
                      <IconButton
                        aria-label="Remove (permanent — 404s everywhere, including the author's profile)"
                        variant="solid"
                        size="md"
                        onClick={busy ? undefined : () => handleReview(row, REVIEW_ACTIONS.remove)}
                      >
                        <TrashSimple size={14} />
                      </IconButton>
                    </div>
                  ) : null}
                  <a href={rowViewHref(row)} className={styles.viewLink}>
                    <ArrowSquareOut size={14} />
                  </a>
                </div>
              </DataRow>
            );
          })}
        </DataTable>

        {!loading && error ? <div className={styles.emptyState}>{error}</div> : null}
        {!loading && !error && rows.length === 0 ? (
          <div className={styles.emptyState}>No flags found.</div>
        ) : null}
      </section>

      <PaginationFooter
        totalLabel={totalLabel}
        pages={pages}
        activePage={activePage}
        hasPrevious={hasPrevious}
        hasNext={hasNext}
        onPrevious={() => setPageIndex((p) => Math.max(1, p - 1))}
        onNext={() => setPageIndex((p) => p + 1)}
        perPage={pageSize}
        perPageOptions={PER_PAGE_OPTIONS}
        onPerPageChange={(option) => {
          setPageSize(option);
          setPageIndex(1);
        }}
        onPageChange={(page) => {
          if (page === activePage) return;
          setPageIndex(page);
        }}
        className={styles.pagination}
      />
    </div>
  );
}
